// Ventanas modales: catálogo de componentes, ejemplos, proyectos guardados, librerías y ayuda.
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { CATEGORIES, PARTS, resolveProps, getDef } from '../parts/index.js';
import { MODELS } from '../scene/models/index.js';
import { EXAMPLES } from '../examples/index.js';
import { api } from '../api.js';
import { escapeHtml } from './panels.js';
import { disposeObject, visibleBounds } from '../scene/kit.js';
import { fmtRelative, fmtDateTime, fmtNum } from './html.js';
import { boardName } from '../aulas.js';
import { loginUrl, registerUrl } from '../session.js';

// Dentro de una tarea (trabajo, plantilla o revisión) los ejemplos se abren en otra pestaña: así no reemplazan
// el trabajo.
const inTask = (app) => ['work', 'template', 'review', 'error'].includes(app.kind);

const modal = () => document.getElementById('modal');

export function openModal(title, html, { narrow = false } = {}) {
  const m = modal();
  m.querySelector('#modal-title').textContent = title;
  const body = m.querySelector('#modal-body');
  body.innerHTML = html;
  m.querySelector('.modal-card').classList.toggle('narrow', narrow);
  m.hidden = false;
  return body;
}

export function closeModal() {
  modal().hidden = true;
  modal().querySelector('#modal-body').innerHTML = '';
}

export function setupModal(app) {
  const m = modal();
  m.addEventListener('click', (e) => {
    if (e.target === m || e.target.closest('[data-close]')) closeModal();
  });
  app.on('escape', () => {
    if (!m.hidden) closeModal();
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !m.hidden) closeModal();
  });
}

// Miniaturas 3D -------------------------------------------------------------------------------------
const thumbs = new Map();
let thumbRenderer = null;
let thumbScene = null;

const THUMB_PREFIX = 'arduino3d.thumb.';
const BUILD = process.env.BUILD_ID;
let thumbStoreChecked = false;

function storedThumb(key) {
  if (process.env.NODE_ENV !== 'production') return null; // en dev los modelos cambian sin cambiar BUILD_ID
  try {
    if (!thumbStoreChecked) {
      // Borra miniaturas de compilaciones anteriores (los modelos pudieron cambiar)
      thumbStoreChecked = true;
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(THUMB_PREFIX) && !k.startsWith(THUMB_PREFIX + BUILD + ':')) localStorage.removeItem(k);
      }
    }
    return localStorage.getItem(THUMB_PREFIX + BUILD + ':' + key);
  } catch {
    return null;
  }
}

function renderThumb(type, props) {
  const key = type + JSON.stringify(props || {});
  if (thumbs.has(key)) return thumbs.get(key);
  const stored = storedThumb(key);
  if (stored) {
    thumbs.set(key, stored);
    return stored;
  }
  if (!thumbRenderer) {
    thumbRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    thumbRenderer.setSize(220, 150);
    thumbRenderer.setPixelRatio(1);
    thumbRenderer.outputColorSpace = THREE.SRGBColorSpace;
    thumbRenderer.toneMapping = THREE.ACESFilmicToneMapping;
    thumbScene = new THREE.Scene();
    const pm = new THREE.PMREMGenerator(thumbRenderer);
    thumbScene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture;
    thumbScene.environmentIntensity = 0.6;
    thumbScene.add(new THREE.HemisphereLight('#ffffff', '#445', 1.3));
    const d = new THREE.DirectionalLight('#fff', 2.2);
    d.position.set(-1, 3, 2);
    thumbScene.add(d);
  }
  const def = getDef(type);
  const build = MODELS[type];
  let url = '';
  if (build) {
    const view = build(def, resolveProps(type, props));
    thumbScene.add(view.root);
    const box = visibleBounds([view.root]);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const cam = new THREE.PerspectiveCamera(30, 220 / 150, 0.5, 5000);
    const r = Math.max(size.x, size.z * 1.2, size.y) * 0.62 + 2;
    const dist = r / Math.tan((15 * Math.PI) / 180);
    cam.position.set(center.x + dist * 0.18, center.y + dist * 0.72, center.z + dist * 0.68);
    cam.lookAt(center);
    thumbRenderer.render(thumbScene, cam);
    url = thumbRenderer.domElement.toDataURL('image/webp', 0.85);
    try {
      if (process.env.NODE_ENV === 'production') localStorage.setItem(THUMB_PREFIX + BUILD + ':' + key, url);
    } catch {
      // almacenamiento lleno o bloqueado: solo se pierde la caché
    }
    thumbScene.remove(view.root);
    if (view.dispose) view.dispose();
    disposeObject(view.root);
  }
  thumbs.set(key, url);
  return url;
}

function catalogEntries() {
  const out = [];
  for (const def of Object.values(PARTS)) {
    if (def.hidden) continue;
    if (def.variants) for (const v of def.variants) out.push({ def, label: v.label, props: v.props, desc: v.description || def.description });
    else out.push({ def, label: def.name, props: {}, desc: def.description });
  }
  return out;
}

export function openCatalog(app) {
  const entries = catalogEntries();
  const body = openModal(
    'Agregar componente',
    `<div class="modal-tools"><input type="search" id="cat-search" placeholder="Buscar: LED, sensor, servo, pantalla…" autocomplete="off"></div><div id="cat-list"></div>`,
  );
  const list = body.querySelector('#cat-list');
  const search = body.querySelector('#cat-search');
  const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const pendingThumbs = [];
  const draw = () => {
    const q = norm(search.value.trim());
    list.innerHTML = '';
    pendingThumbs.length = 0;
    for (const cat of CATEGORIES) {
      const items = entries.filter((e) => e.def.category === cat.id && (!q || norm(e.label + ' ' + (e.desc || '') + ' ' + e.def.type + ' ' + (e.def.keywords || '')).includes(q)));
      if (!items.length) continue;
      const h = document.createElement('div');
      h.className = 'cat-title';
      h.textContent = cat.name;
      list.appendChild(h);
      const grid = document.createElement('div');
      grid.className = 'cards';
      for (const it of items) {
        const card = document.createElement('button');
        card.className = 'card';
        card.innerHTML = `<div class="thumb"></div><div class="body"><div class="name">${escapeHtml(it.label)}</div><div class="desc">${escapeHtml(it.desc || '')}</div></div>`;
        card.onclick = () => {
          closeModal();
          app.interaction.startPlacing(it.def.type, it.props);
        };
        grid.appendChild(card);
        pendingThumbs.push([card.querySelector('.thumb'), it]);
      }
      list.appendChild(grid);
    }
    if (!list.children.length) list.innerHTML = '<div class="empty">No hay componentes con ese nombre.</div>';
    const step = () => {
      const job = pendingThumbs.shift();
      if (!job) return;
      const [el, it] = job;
      const url = renderThumb(it.def.type, it.props);
      if (url) el.innerHTML = `<img alt="" src="${url}">`;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  search.addEventListener('input', draw);
  draw();
  setTimeout(() => search.focus(), 30);
}

// Ejemplos ------------------------------------------------------------------------------------------
export function openExamples(app) {
  const cats = [...new Set(EXAMPLES.map((e) => e.category))];
  const body = openModal(
    'Ejemplos',
    `${inTask(app) ? '<p class="modal-note">Los ejemplos se abren en otra pestaña: tu trabajo en esta tarea no cambia.</p>' : ''}<div class="modal-tools"><input type="search" id="ex-search" placeholder="Buscar ejemplo…" autocomplete="off" aria-label="Buscar ejemplo"></div><div id="ex-list"></div>`,
  );
  const list = body.querySelector('#ex-list');
  const search = body.querySelector('#ex-search');
  const pending = [];
  const draw = () => {
    const q = search.value.trim().toLowerCase();
    list.innerHTML = '';
    pending.length = 0;
    for (const c of cats) {
      const items = EXAMPLES.filter((e) => e.category === c && (!q || (e.name + ' ' + e.description).toLowerCase().includes(q)));
      if (!items.length) continue;
      list.insertAdjacentHTML('beforeend', `<div class="cat-title">${escapeHtml(c)}</div>`);
      const grid = document.createElement('div');
      grid.className = 'cards';
      for (const ex of items) {
        const card = document.createElement('button');
        card.className = 'card';
        const types = [...new Set(ex.project.parts.map((p) => p.type))].filter((t) => getDef(t).kind !== 'breadboard');
        card.innerHTML = `<div class="thumb"></div><div class="body"><div class="name">${escapeHtml(ex.name)}</div><div class="desc">${escapeHtml(ex.description)}</div><div class="desc" style="margin-top:6px;color:var(--faint)">${escapeHtml(types.map((t) => getDef(t).name).join(' · '))}</div></div>`;
        // Miniatura de la pieza más representativa del ejemplo
        const boring = new Set(['resistor', 'led', 'pushbutton']);
        const main = ex.project.parts.find((p) => !['board', 'breadboard'].includes(getDef(p.type).kind) && !boring.has(p.type)) ||
          ex.project.parts.find((p) => !['board', 'breadboard'].includes(getDef(p.type).kind)) || ex.project.parts[0];
        pending.push([card.querySelector('.thumb'), main]);
        card.onclick = () => {
          if (inTask(app)) {
            window.open('/?ejemplo=' + encodeURIComponent(ex.id), '_blank', 'noopener');
            return;
          }
          if (!confirmDiscard(app)) return;
          closeModal();
          app.openExample(ex.id);
        };
        grid.appendChild(card);
      }
      list.appendChild(grid);
    }
    const step = () => {
      const job = pending.shift();
      if (!job) return;
      const url = renderThumb(job[1].type, job[1].props || {});
      if (url) job[0].innerHTML = `<img alt="" src="${url}">`;
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  search.addEventListener('input', draw);
  draw();
}

export function confirmDiscard(app) {
  if (app.projectId || !app.hasUserChanges) return true;
  return confirm('Tienes cambios sin guardar en este proyecto. ¿Descartarlos?');
}

// Proyectos guardados ---------------------------------------------------------------------------
const LOCK = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zm2 0h6V7a3 3 0 0 0-6 0z"/></svg>';

export async function openProjects(app) {
  const body = openModal(
    'Mis proyectos',
    '<div class="modal-tools"><button class="btn primary" data-board="arduino-uno">+ Nuevo con Uno</button><button class="btn" data-board="arduino-nano">+ Nuevo con Nano</button><button class="btn" data-board="arduino-mega">+ Nuevo con Mega 2560</button><button class="btn" data-board="pi-pico">+ Nuevo con Pico</button></div><p class="modal-note" id="pj-count" hidden></p><div id="pj-list"><div class="empty">Cargando…</div></div>',
  );
  body.querySelectorAll('[data-board]').forEach((b) => {
    b.onclick = () => {
      if (!confirmDiscard(app)) return;
      closeModal();
      app.newProject(b.dataset.board);
    };
  });
  const list = body.querySelector('#pj-list');
  // Los proyectos son de cada cuenta: sin sesión no hay lista.
  if (!app.user) {
    const back = location.pathname + location.search;
    list.innerHTML = `<div class="empty">Tus proyectos se guardan en tu cuenta. <a href="${escapeHtml(loginUrl(back))}" data-leave data-nosave>Entra</a> o <a href="${escapeHtml(registerUrl(back))}" data-leave data-nosave>crea una cuenta</a> para verlos y guardar.<br>Sin cuenta puedes usar el simulador y los ejemplos; tu trabajo queda en este navegador.</div>`;
    return;
  }
  let items;
  try {
    items = await api.get('/api/projects');
  } catch (e) {
    list.innerHTML = `<div class="empty">${escapeHtml(e.message)}</div>`;
    return;
  }
  // Cuántos proyectos guarda el plan (docs/PLANES.md): al llegar al tope, para guardar otro hay que borrar alguno.
  const plan = app.plan;
  const countEl = body.querySelector('#pj-count');
  let total = items.length;
  const drawCount = () => {
    if (!plan) return;
    const full = total >= plan.projects;
    countEl.hidden = false;
    countEl.classList.toggle('warn', full);
    countEl.textContent = `Plan ${plan.name === 'maker' ? 'Maker' : 'Gratis'}: ${total} de ${plan.projects} proyectos guardados.${full ? ' Para guardar otro, borra alguno.' : ''}`;
  };
  drawCount();
  if (!items.length) {
    list.innerHTML = '<div class="empty">Aún no tienes proyectos guardados. Abre un ejemplo o crea uno nuevo y usa «Guardar» (Ctrl+S).</div>';
    return;
  }
  list.innerHTML = '<div class="list"></div>';
  const ul = list.firstChild;
  for (const p of items) {
    const row = document.createElement('div');
    row.className = 'list-item';
    const meta = [boardName(p.board), `${p.parts} ${p.parts === 1 ? 'pieza' : 'piezas'}`, p.updatedAt ? `guardado ${fmtRelative(p.updatedAt)}` : ''].filter(Boolean).join(' · ');
    const lock = p.visibility === 'private' ? ` <span class="vis-tag" title="Solo tú lo abres">${LOCK}Privado</span>` : '';
    row.innerHTML = `<div class="grow"><div class="t">${escapeHtml(p.name)}${lock}${p.id === app.projectId ? ' <span style="color:var(--accent)">(abierto)</span>' : ''}</div><div class="s" title="${escapeHtml(fmtDateTime(p.updatedAt))}">${escapeHtml(meta)}</div></div><button class="btn small" data-open>Abrir</button><button class="btn small danger" data-del>Eliminar</button>`;
    row.querySelector('[data-open]').onclick = async () => {
      if (!confirmDiscard(app)) return;
      closeModal();
      await app.openProject(p.id);
    };
    row.querySelector('[data-del]').onclick = async () => {
      if (!confirm(`¿Eliminar «${p.name}»? No se puede deshacer y quien tenga el enlace ya no podrá abrirlo.`)) return;
      try {
        await api.del(`/api/projects/${p.id}`);
      } catch (e) {
        app.toast('No se pudo eliminar: ' + e.message, true);
        return;
      }
      row.remove();
      total--;
      drawCount();
      if (p.id === app.projectId) {
        // El proyecto abierto ya no existe en el servidor: queda como proyecto sin guardar.
        app.projectId = null;
        app.dirty = true;
        app.hasUserChanges = true;
        app.emit('save-state');
        history.replaceState(null, '', '/');
      }
    };
    ul.appendChild(row);
  }
}

// Librerías -------------------------------------------------------------------------------------------
// Preinstaladas (vienen con el simulador), para todos (las instala el administrador) y mías (las de la cuenta).
// Instalar las propias es del plan Maker; el administrador elige si instala en su cuenta o para todos. docs/PLANES.md,
// «Librerías por cuenta».
const LIB_SECTIONS = [['cuenta', 'Mías'], ['global', 'Para todos'], ['preinstalada', 'Preinstaladas']];
const LIB_WHERE = { cuenta: 'en tu cuenta', global: 'para todos', preinstalada: 'preinstalada' };
const LIB_RANK = { cuenta: 0, global: 1, preinstalada: 2 }; // al compilar, si dos se llaman igual, se prefiere la primera
const MAKER_LIBS = 'Instalar tus propias librerías es del plan Maker.';
const MB = 1024 * 1024;
const OWN_LIBS = { count: 30, bytes: 100 * MB }; // los topes de la cuenta (OWN_LIBRARIES de server/plans.js)

/** «35 KB», «4.2 MB». */
function fmtBytes(n) {
  return n < MB / 10 ? `${n ? Math.max(1, Math.round(n / 1024)) : 0} KB` : `${fmtNum(n / MB)} MB`;
}

const lastLine = (output) => String(output || '').trim().split('\n').pop();

export async function openLibraries(app) {
  const user = app.user;
  const body = openModal(
    'Librerías de Arduino',
    `<p class="modal-note">Las instaladas se usan con <code>#include</code>. Puedes buscar más en el índice oficial de Arduino.</p>
     <div id="lib-plan"></div>
     <div class="modal-tools"><input type="search" id="lib-q" placeholder="Filtra las instaladas o busca en el índice…" autocomplete="off" aria-label="Filtrar las instaladas o buscar en el índice"><button class="btn" id="lib-search">Buscar en el índice</button></div>
     ${user && user.admin ? `<fieldset class="lib-scope"><legend>Instalar:</legend>
       <label><input type="radio" name="lib-scope" value="cuenta"> Para mi cuenta</label>
       <label><input type="radio" name="lib-scope" value="global"> Para todos</label></fieldset>` : ''}
     <p class="notice" id="lib-msg" role="status" hidden></p>
     <div id="lib-results"></div><div id="lib-list"><div class="empty">Cargando…</div></div>`,
  );
  const planEl = body.querySelector('#lib-plan');
  const listEl = body.querySelector('#lib-list');
  const results = body.querySelector('#lib-results');
  const msg = body.querySelector('#lib-msg');
  const q = body.querySelector('#lib-q');
  const scopes = [...body.querySelectorAll('input[name=lib-scope]')];
  let plan = app.plan;
  let usage = null;
  let installed = [];
  let found = null; // resultados de la última búsqueda en el índice
  const busy = new Set(); // las que se están instalando
  const own = () => !!(plan && plan.ownLibraries);
  // Dónde se instala: el administrador lo elige; los demás, en su cuenta.
  const scope = () => (scopes.find((r) => r.checked) || { value: 'cuenta' }).value;
  const canInstall = () => !!user && (scope() === 'global' ? !!user.admin : own());
  const removable = (l) => !!user && (l.location === 'cuenta' || (l.location === 'global' && user.admin));
  const say = (text, kind = 'info') => {
    msg.className = 'notice ' + kind;
    msg.textContent = text;
    msg.hidden = !text;
  };

  // Lo que deja hacer el plan: cuántas librerías propias lleva o por qué no puede instalar (#lib-why).
  const drawPlan = () => {
    if (!user) {
      planEl.innerHTML = `<p class="notice info" id="lib-why"><a href="${escapeHtml(loginUrl(location.pathname + location.search))}" data-leave data-nosave>Entra con tu cuenta</a> para instalar librerías (instalar tus propias librerías es del plan Maker).</p>`;
      return;
    }
    if (own()) {
      const used = usage && usage.libraries;
      const bar = (n, max) => `<i aria-hidden="true"><b style="width:${Math.min(100, (100 * n) / max)}%"></b></i>`;
      planEl.innerHTML = used
        ? `<div class="lib-usage"><span class="meter">Tus librerías ${bar(used.count, OWN_LIBS.count)}<b>${used.count} de ${OWN_LIBS.count}</b></span>
            <span class="meter">Espacio ${bar(used.bytes, OWN_LIBS.bytes)}<b>${fmtBytes(used.bytes)} de ${OWN_LIBS.bytes / MB} MB</b></span></div>`
        : `<p class="notice info">Puedes instalar librerías en tu cuenta: hasta ${OWN_LIBS.count} y ${OWN_LIBS.bytes / MB} MB.</p>`;
    } else {
      const leftover = usage && usage.libraries && usage.libraries.count;
      planEl.innerHTML = `<p class="notice info" id="lib-why">${MAKER_LIBS} ${user.admin ? 'Como administrador, puedes instalar para todos.' : 'Puedes buscar en el índice y usar las preinstaladas y las que son para todos.'}${leftover ? ' Las tuyas siguen compilando y puedes desinstalarlas.' : ''}</p>`;
    }
    if (scopes.length) {
      // Sin Maker, el administrador solo instala para todos.
      const mine = scopes[0];
      mine.disabled = !own();
      if (mine.disabled) mine.setAttribute('aria-describedby', 'lib-why');
      else mine.removeAttribute('aria-describedby');
      if (!scopes.some((r) => r.checked && !r.disabled)) scopes[own() ? 0 : 1].checked = true;
    }
  };

  const row = (l) => {
    // Si hay otra con el mismo nombre más abajo en LIB_RANK, al compilar se prefiere esta.
    const other = installed.find((o) => o.name === l.name && LIB_RANK[o.location] > LIB_RANK[l.location]);
    return `<div class="list-item"><div class="grow"><div class="t">${escapeHtml(l.name)} <span style="color:var(--faint);font-weight:400">${escapeHtml(l.version)}</span></div><div class="s">${escapeHtml(l.sentence || '')}${l.includes ? ` · <code>#include &lt;${escapeHtml(l.includes.split(',')[0])}&gt;</code>` : ''}</div></div>${other ? `<span class="s" title="${escapeHtml(`También está ${LIB_WHERE[other.location]} (${other.version}): al compilar se prefiere esta.`)}">repetida</span>` : ''}${removable(l) ? `<button class="btn small" data-uninstall="${escapeHtml(l.name)}" data-scope="${l.location}">Desinstalar</button>` : ''}</div>`;
  };
  const drawInstalled = () => {
    const f = q.value.trim().toLowerCase();
    const match = (l) => !f || (l.name + ' ' + l.sentence + ' ' + l.author).toLowerCase().includes(f);
    let html = '';
    for (const [loc, title] of LIB_SECTIONS) {
      const items = installed.filter((l) => l.location === loc && match(l));
      // «Mías» se ve con Maker aunque esté vacía: ahí quedan las que instales.
      const empty = !items.length && loc === 'cuenta' && own() && !f;
      if (!items.length && !empty) continue;
      html += `<div class="cat-title">${title}</div>`;
      html += empty ? '<p class="muted small lib-empty">Aún no instalas librerías en tu cuenta: búscalas en el índice.</p>' : `<div class="list">${items.map(row).join('')}</div>`;
    }
    listEl.innerHTML = html || '<div class="empty">Ninguna instalada coincide. Presiona Enter para buscarla en el índice.</div>';
  };
  const drawResults = () => {
    if (!found) return;
    if (!found.length) {
      results.innerHTML = '<div class="empty">Sin resultados en el índice.</div>';
      return;
    }
    const can = canInstall();
    results.innerHTML = `<div class="cat-title">Resultados del índice</div><div class="list">${found.map((l) => {
      const have = installed.find((i) => i.name === l.name);
      const action = have
        ? `<span class="s">${have.location === 'preinstalada' ? 'Preinstalada' : `Instalada ${LIB_WHERE[have.location]}`}</span>`
        : busy.has(l.name)
          ? '<button class="btn small" disabled>Instalando…</button>'
          : `<button class="btn small${can ? ' primary' : ''}" data-install="${escapeHtml(l.name)}"${can ? '' : ' disabled aria-describedby="lib-why"'}>Instalar</button>`;
      return `<div class="list-item"><div class="grow"><div class="t">${escapeHtml(l.name)} <span style="color:var(--faint);font-weight:400">${escapeHtml(l.version || '')}</span></div><div class="s">${escapeHtml(l.sentence || '')} ${l.author ? '· ' + escapeHtml(l.author) : ''}</div></div>${action}</div>`;
    }).join('')}</div>`;
  };

  const load = async () => {
    const [libs, p] = await Promise.all([
      api.get('/api/libraries').catch((e) => e),
      user ? app.refreshPlan().catch(() => null) : null,
    ]);
    if (p) {
      plan = p;
      usage = p.usage;
    }
    drawPlan();
    if (libs instanceof Error) listEl.innerHTML = `<div class="empty">${escapeHtml(libs.message)}</div>`;
    else {
      installed = libs;
      drawInstalled();
    }
    drawResults();
  };

  // Los mensajes del servidor (topes de la cuenta, otra instalación en curso, sin Maker…) se muestran tal cual.
  results.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-install]');
    if (!b || b.disabled) return;
    const name = b.dataset.install;
    const where = scope();
    busy.add(name);
    drawResults();
    say(`Instalando «${name}» ${LIB_WHERE[where]}… Puede tardar un minuto.`);
    try {
      const r = await api.post('/api/libraries/install', { name, scope: where });
      if (r.ok) say(`Librería «${name}» instalada ${LIB_WHERE[where]}.`, 'ok');
      else say('No se pudo instalar: ' + lastLine(r.output), 'error');
    } catch (err) {
      say(err.message, 'error');
    }
    busy.delete(name);
    await load();
  });
  listEl.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-uninstall]');
    if (!b) return;
    const name = b.dataset.uninstall;
    const where = b.dataset.scope === 'global' ? 'global' : 'cuenta';
    if (!confirm(`¿Desinstalar «${name}»${where === 'global' ? ' para todos' : ''}? Los sketches que la incluyan dejarán de compilar.`)) return;
    b.disabled = true;
    b.textContent = 'Desinstalando…';
    try {
      const r = await api.post('/api/libraries/uninstall', { name, scope: where });
      if (r.ok) say(`Librería «${name}» desinstalada.`, 'ok');
      else say('No se pudo desinstalar: ' + lastLine(r.output), 'error');
    } catch (err) {
      say(err.message, 'error');
    }
    await load();
  });
  scopes.forEach((r) => r.addEventListener('change', drawResults));
  q.addEventListener('input', drawInstalled);
  const doSearch = async () => {
    const term = q.value.trim();
    if (term.length < 2) return;
    results.innerHTML = '<div class="empty">Buscando…</div>';
    try {
      found = await api.get('/api/libraries/search?q=' + encodeURIComponent(term));
      drawResults();
    } catch (e) {
      found = null;
      results.innerHTML = `<div class="empty">${escapeHtml(e.message)}</div>`;
    }
  };
  body.querySelector('#lib-search').onclick = doSearch;
  q.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') doSearch();
  });
  if (!user) drawPlan(); // con cuenta, después de leer su plan y su uso
  load();
}

// Ayuda --------------------------------------------------------------------------------------------
export function openHelp() {
  openModal(
    'Cómo se usa',
    `<div class="help-grid">
      <b>Agregar piezas</b><span>Botón «Componentes»: elige una y haz clic donde la quieras. Sobre la protoboard sus patas se insertan solas en los agujeros.</span>
      <b>Cables</b><span>Clic en un pin (o agujero) y luego clic en otro. El color se elige arriba; GND sale negro y 5 V rojo.</span>
      <b>Mover</b><span>Arrastra la pieza. <kbd>R</kbd> la gira 90°, <kbd>Supr</kbd> la elimina, <kbd>Ctrl D</kbd> la duplica.</span>
      <b>Cámara</b><span>Arrastra el fondo para girar, clic derecho para desplazar y la rueda para acercar. Botones 3D, Arriba y Encuadrar.</span>
      <b>Simular</b><span>«Simular» (<kbd>Ctrl Enter</kbd>) compila tu código con arduino-cli y lo corre en un ATmega328P emulado (el ATmega2560 en el Mega, el RP2040 en la Raspberry Pi Pico). Puedes cambiar el circuito mientras corre.</span>
      <b>Interactuar</b><span>Presiona botones, gira perillas y cambia interruptores con el mouse. Los sensores se ajustan en el panel de propiedades.</span>
      <b>Medir</b><span>Con la simulación corriendo, pasa el cursor sobre un pin para ver su voltaje.</span>
      <b>Deshacer</b><span><kbd>Ctrl Z</kbd> / <kbd>Ctrl Y</kbd> para el circuito; el editor tiene su propio historial.</span>
      <b>Guardar</b><span><kbd>Ctrl S</kbd> guarda en el servidor; después se guarda solo con cada cambio.</span>
    </div>`,
    { narrow: true },
  );
}

// Importar de Wokwi ---------------------------------------------------------------------------------
export function openWokwiImport(app) {
  const body = openModal(
    'Importar un proyecto de Wokwi',
    `<p style="margin-top:0;color:var(--muted)">Elige los archivos del proyecto (<code>diagram.json</code>, <code>sketch.ino</code> y, si los hay, <code>.h</code>/<code>.cpp</code> y <code>libraries.txt</code>) o pega su contenido. Las piezas se acomodan en 3D y las que estaban en la protoboard se insertan en los mismos agujeros.</p>
     <div class="modal-tools"><input type="file" id="wk-files" multiple accept=".json,.ino,.h,.hpp,.c,.cpp,.txt"></div>
     <label class="lbl" style="display:block;color:var(--muted);margin:8px 0 4px">diagram.json</label>
     <textarea id="wk-diagram" rows="6" spellcheck="false" style="width:100%;font:12px var(--mono);background:var(--panel-2);border:1px solid var(--line-2);border-radius:8px;padding:8px"></textarea>
     <label class="lbl" style="display:block;color:var(--muted);margin:8px 0 4px">sketch.ino</label>
     <textarea id="wk-sketch" rows="6" spellcheck="false" style="width:100%;font:12px var(--mono);background:var(--panel-2);border:1px solid var(--line-2);border-radius:8px;padding:8px"></textarea>
     <div class="modal-tools" style="margin-top:12px"><button class="btn primary" id="wk-go">Importar</button><span id="wk-msg" style="color:var(--muted);align-self:center"></span></div>`,
  );
  const extra = {};
  let libraries = [];
  body.querySelector('#wk-files').addEventListener('change', async (e) => {
    for (const f of e.target.files) {
      const text = await f.text();
      if (f.name === 'diagram.json') body.querySelector('#wk-diagram').value = text;
      else if (f.name.endsWith('.ino')) body.querySelector('#wk-sketch').value = text;
      else if (f.name === 'libraries.txt') libraries = text.split('\n').map((l) => l.trim()).filter((l) => l && !l.startsWith('#')).map((l) => l.replace(/@.*$/, ''));
      else if (/\.(h|hpp|c|cpp)$/.test(f.name)) extra[f.name.replace(/[^\w.-]/g, '_')] = text;
    }
  });
  body.querySelector('#wk-go').addEventListener('click', async () => {
    const msg = body.querySelector('#wk-msg');
    let diagram;
    try {
      diagram = JSON.parse(body.querySelector('#wk-diagram').value);
    } catch {
      msg.textContent = 'El diagram.json no es JSON válido.';
      return;
    }
    const sketch = body.querySelector('#wk-sketch').value || 'void setup() {}\nvoid loop() {}\n';
    let result;
    try {
      const { importWokwi } = await import('../wokwi.js');
      result = importWokwi(diagram, { 'sketch.ino': sketch, ...extra }, diagram.name || 'Proyecto de Wokwi');
    } catch (err) {
      msg.textContent = err.message;
      return;
    }
    if (!confirmDiscard(app)) return;
    closeModal();
    app.resetMode();
    app.load(result.project, { id: null });
    app.hasUserChanges = true;
    app.markDirty();
    const notes = [];
    if (result.skipped.length) notes.push(`piezas sin equivalente: ${result.skipped.join(', ')}`);
    if (result.dropped) notes.push(`${result.dropped} conexiones omitidas`);
    app.toast(notes.length ? 'Importado con avisos: ' + notes.join('; ') : 'Proyecto de Wokwi importado', notes.length > 0);
    if (libraries.length) installMissing(app, libraries);
  });
}

/** Librerías de libraries.txt que no están instaladas: se instalan si el plan lo permite (docs/PLANES.md). */
async function installMissing(app, names) {
  let installed = [];
  try {
    installed = (await api.get('/api/libraries')).map((l) => l.name.toLowerCase());
  } catch {
    return;
  }
  const missing = names.filter((n) => !installed.includes(n.toLowerCase()));
  if (!missing.length) return;
  const list = missing.join(', ');
  // Con Maker, en la cuenta; el administrador sin Maker, para todos. Si no, se dice por qué no.
  if (app.user) await app.refreshPlan().catch(() => {});
  const scope = app.plan && app.plan.ownLibraries ? 'cuenta' : app.user && app.user.admin ? 'global' : null;
  if (!scope) {
    app.toast(`Este proyecto usa librerías que no están instaladas (${list}). ${app.user ? MAKER_LIBS : 'Entra con tu cuenta para instalarlas (instalar tus propias librerías es del plan Maker).'}`, true);
    return;
  }
  const failed = [];
  for (const name of missing) {
    app.toast(`Instalando la librería «${name}» ${LIB_WHERE[scope]}…`);
    try {
      const r = await api.post('/api/libraries/install', { name, scope });
      if (!r.ok) failed.push(name); // p. ej. no está en el índice: las demás sí se intentan
    } catch (e) {
      // Tal cual: los topes de la cuenta, otra instalación en curso (409) o el plan (403). Las demás tampoco entrarían.
      app.toast(e.message, true);
      return;
    }
  }
  if (failed.length) app.toast(`No se pudo instalar: ${failed.map((n) => `«${n}»`).join(', ')}`, true);
  else app.toast(`${missing.length === 1 ? 'Librería instalada' : 'Librerías instaladas'} ${LIB_WHERE[scope]}: ${list}`);
}
